/**
 * Memory Monitoring Utility for Apollo Package
 *
 * Server-side memory profiling and monitoring utilities to track memory usage
 * and validate memory leak fixes. Only runs on server-side (Node.js).
 *
 * Features:
 * - Track heap usage, external memory, and RSS
 * - Log memory stats periodically
 * - Warning thresholds for memory usage
 * - Optional monitoring via environment variable
 *
 * Usage:
 * - Set ENABLE_MEMORY_MONITORING=true to enable monitoring
 * - Optionally set MEMORY_MONITOR_SAMPLE_RATE (0-1) to control sampling
 * - Call getMemoryStats() to get current memory usage
 * - Call logMemoryStats() to log current stats
 * - Call checkMemoryThreshold() to check if memory exceeds thresholds
 */

export interface MemoryStats {
  rss: number // Resident Set Size (total memory allocated)
  heapTotal: number // Total heap size
  heapUsed: number // Heap memory actually used
  external: number // Memory used by C++ objects bound to JavaScript
  arrayBuffers: number // Memory allocated for ArrayBuffers and SharedArrayBuffers
  timestamp: number
  formattedRss: string
  formattedHeapTotal: string
  formattedHeapUsed: string
  formattedExternal: string
}

/**
 * Check if memory monitoring is enabled
 */
const DEFAULT_SAMPLE_RATE = process.env.NODE_ENV === "production" ? 0.05 : 1

type MemoryMonitorGlobalScope = typeof globalThis & {
  __eggedMemoryMonitorSamplingDecision?: boolean
}

const getGlobalScope = (): MemoryMonitorGlobalScope =>
  globalThis as MemoryMonitorGlobalScope

const getSharedSamplingDecision = (): boolean | undefined =>
  getGlobalScope().__eggedMemoryMonitorSamplingDecision

const setSharedSamplingDecision = (value: boolean | undefined): void => {
  getGlobalScope().__eggedMemoryMonitorSamplingDecision = value
}

export function isMemoryMonitoringEnabled(): boolean {
  return (
    typeof window === "undefined" &&
    process.env.ENABLE_MEMORY_MONITORING === "true"
  )
}

export function getMemoryMonitorSampleRate(): number {
  const rawRate = process.env.MEMORY_MONITOR_SAMPLE_RATE
  if (!rawRate) {
    return DEFAULT_SAMPLE_RATE
  }

  const parsed = Number(rawRate)
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return DEFAULT_SAMPLE_RATE
  }

  return parsed
}

export function shouldCollectMemoryTelemetry(): boolean {
  if (!isMemoryMonitoringEnabled()) {
    return false
  }

  const currentDecision = getSharedSamplingDecision()

  if (typeof currentDecision === "undefined") {
    const nextDecision = Math.random() < getMemoryMonitorSampleRate()
    setSharedSamplingDecision(nextDecision)
    return nextDecision
  }

  return currentDecision
}

export function resetMemoryMonitorSampling(forceDecision?: boolean): void {
  if (typeof forceDecision === "boolean") {
    setSharedSamplingDecision(forceDecision)
    return
  }

  setSharedSamplingDecision(undefined)
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  const mb = bytes / 1024 / 1024
  return `${mb.toFixed(2)} MB`
}

/**
 * Get current memory statistics
 */
export function getMemoryStats(): MemoryStats {
  // Edge Runtime check - return empty stats if not in Node.js
  if (typeof process === "undefined" || !process.memoryUsage) {
    return {} as MemoryStats
  }

  if (typeof window !== "undefined") {
    throw new Error("Memory monitoring is only available on server-side")
  }

  const memUsage = process.memoryUsage()

  return {
    rss: memUsage.rss,
    heapTotal: memUsage.heapTotal,
    heapUsed: memUsage.heapUsed,
    external: memUsage.external,
    arrayBuffers: memUsage.arrayBuffers,
    timestamp: Date.now(),
    formattedRss: formatBytes(memUsage.rss),
    formattedHeapTotal: formatBytes(memUsage.heapTotal),
    formattedHeapUsed: formatBytes(memUsage.heapUsed),
    formattedExternal: formatBytes(memUsage.external),
  }
}

/**
 * Log memory statistics to console
 */
export function logMemoryStats(context?: string): MemoryStats {
  if (!shouldCollectMemoryTelemetry()) {
    return {} as MemoryStats
  }

  const stats = getMemoryStats()
  const prefix = context ? `[Memory Monitor - ${context}]` : "[Memory Monitor]"

  console.log(prefix, "Memory Usage:")
  console.log(`  RSS: ${stats.formattedRss}`)
  console.log(`  Heap Total: ${stats.formattedHeapTotal}`)
  console.log(`  Heap Used: ${stats.formattedHeapUsed}`)
  console.log(`  External: ${stats.formattedExternal}`)
  console.log(
    `  Heap Usage: ${((stats.heapUsed / stats.heapTotal) * 100).toFixed(1)}%`,
  )

  return stats
}

/**
 * Memory thresholds for warnings
 */
export interface MemoryThresholds {
  heapUsedMB?: number // Warn if heap used exceeds this (in MB)
  heapUsagePercent?: number // Warn if heap usage % exceeds this
  rssMB?: number // Warn if RSS exceeds this (in MB)
}

/**
 * Default memory thresholds
 */
export const DEFAULT_THRESHOLDS: MemoryThresholds = {
  heapUsedMB: 1024, // 1GB
  heapUsagePercent: 90, // 90%
  rssMB: 2048, // 2GB
}

/**
 * Check if memory usage exceeds thresholds and log warning
 * Returns true if any threshold is exceeded
 */
export function checkMemoryThreshold(
  context?: string,
  thresholds: MemoryThresholds = DEFAULT_THRESHOLDS,
): boolean {
  if (!shouldCollectMemoryTelemetry()) {
    return false
  }

  const stats = getMemoryStats()
  const heapUsedMB = stats.heapUsed / 1024 / 1024
  const heapUsagePercent = (stats.heapUsed / stats.heapTotal) * 100
  const rssMB = stats.rss / 1024 / 1024

  let thresholdExceeded = false
  const warnings: string[] = []

  if (thresholds.heapUsedMB && heapUsedMB > thresholds.heapUsedMB) {
    warnings.push(
      `Heap used (${heapUsedMB.toFixed(2)} MB) exceeds threshold (${thresholds.heapUsedMB} MB)`,
    )
    thresholdExceeded = true
  }

  if (
    thresholds.heapUsagePercent &&
    heapUsagePercent > thresholds.heapUsagePercent
  ) {
    warnings.push(
      `Heap usage (${heapUsagePercent.toFixed(1)}%) exceeds threshold (${thresholds.heapUsagePercent}%)`,
    )
    thresholdExceeded = true
  }

  if (thresholds.rssMB && rssMB > thresholds.rssMB) {
    warnings.push(
      `RSS (${rssMB.toFixed(2)} MB) exceeds threshold (${thresholds.rssMB} MB)`,
    )
    thresholdExceeded = true
  }

  if (thresholdExceeded) {
    const prefix = context
      ? `[Memory Monitor WARNING - ${context}]`
      : "[Memory Monitor WARNING]"
    console.warn(prefix, "Memory thresholds exceeded:")
    warnings.forEach((warning) => console.warn(`  ${warning}`))
    console.warn("Current stats:")
    console.warn(`  RSS: ${stats.formattedRss}`)
    console.warn(`  Heap Total: ${stats.formattedHeapTotal}`)
    console.warn(`  Heap Used: ${stats.formattedHeapUsed}`)
    console.warn(`  External: ${stats.formattedExternal}`)
  }

  return thresholdExceeded
}

/**
 * Track memory delta between two measurements
 */
export function getMemoryDelta(
  before: MemoryStats,
  after: MemoryStats,
): {
  rssDelta: number
  heapUsedDelta: number
  formattedRssDelta: string
  formattedHeapUsedDelta: string
} {
  const rssDelta = after.rss - before.rss
  const heapUsedDelta = after.heapUsed - before.heapUsed

  return {
    rssDelta,
    heapUsedDelta,
    formattedRssDelta: formatBytes(Math.abs(rssDelta)),
    formattedHeapUsedDelta: formatBytes(Math.abs(heapUsedDelta)),
  }
}

/**
 * Log memory delta between two measurements
 */
export function logMemoryDelta(
  before: MemoryStats,
  after: MemoryStats,
  context?: string,
): void {
  if (!shouldCollectMemoryTelemetry()) {
    return
  }

  const delta = getMemoryDelta(before, after)
  const prefix = context ? `[Memory Delta - ${context}]` : "[Memory Delta]"

  console.log(prefix)
  console.log(
    `  RSS: ${delta.rssDelta >= 0 ? "+" : "-"}${delta.formattedRssDelta}`,
  )
  console.log(
    `  Heap Used: ${delta.heapUsedDelta >= 0 ? "+" : "-"}${delta.formattedHeapUsedDelta}`,
  )
}

/**
 * Cache statistics interface
 */
export interface CacheStats {
  sizeBytes: number
  sizeMB: number
  entryCount: number
  distribution: Record<string, number>
  exceedsThreshold?: boolean
  thresholds?: {
    sizeMB: number
    entryCount: number
  }
}

/**
 * Log Apollo cache statistics
 */
export function logCacheStats(context: string, cacheStats: CacheStats): void {
  if (!shouldCollectMemoryTelemetry()) {
    return
  }

  const prefix = `[Cache Stats - ${context}]`

  console.log(prefix, "Apollo Cache Metrics:")
  console.log(
    `  Size: ${formatBytes(cacheStats.sizeBytes)} (${cacheStats.sizeMB.toFixed(2)} MB)`,
  )
  console.log(`  Entry Count: ${cacheStats.entryCount}`)

  if (cacheStats.thresholds) {
    console.log(
      `  Thresholds: ${cacheStats.thresholds.sizeMB} MB / ${cacheStats.thresholds.entryCount} entries`,
    )
  }

  if (cacheStats.exceedsThreshold) {
    console.warn(`  ⚠️  Cache exceeds threshold!`)
  }

  const topTypes = Object.entries(cacheStats.distribution)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)

  if (topTypes.length > 0) {
    console.log(`  Top Types:`)
    topTypes.forEach(([typename, count]) => {
      console.log(`    ${typename}: ${count}`)
    })
  }
}
